import type React from "react"
import { normalizarId } from "../../hooks/useScope"
import { useTranslation } from "react-i18next"
import { useState, useCallback, useMemo } from "react"
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Grid,
  useTheme,
  useMediaQuery,
  IconButton,
} from "@mui/material"
import {
  Close as CloseIcon,
} from "@mui/icons-material"
import { Chip } from "@mui/material"
import EntityList from "../../components/EntityList"
import type { FiltroCampo } from "../../components/EntityList"
import api from "../../services/api"
import { Tenant } from "../../interfaces"
import { useIsMobile, useIsTablet } from "../../hooks/useMediaQuery"
import { useCryptoNavigation } from "../../App"

export default function TenantList() {
  const { t } = useTranslation()
  console.log("🔄 TenantList render");
  
  const { navigateToPage } = useCryptoNavigation()
  
  const theme = useTheme()
  const isMobile = useIsMobile()
  const isTablet = useIsTablet()
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"))

  const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null)
  const [openDetailsDialog, setOpenDetailsDialog] = useState(false)
  const [refreshTrigger, setRefreshTrigger] = useState(0)

  // Memoizar funções de navegação
  const handleNew = useCallback(() => {
    console.log("🚀 Navegando para novo proprietário");
    navigateToPage('proprietarios', { mode: 'new' });
  }, [navigateToPage]);

  const handleEdit = useCallback((tenantId: number | string) => {
    console.log("✏️ Navegando para editar proprietário ID:", tenantId);
    navigateToPage('proprietarios', { mode: 'edit', id: String(tenantId) });
  }, [navigateToPage]);

  // Função para navegar para módulos relacionados
  const handleNavigateToModule = useCallback((module: string, tenantId: number | undefined) => {
    if (tenantId === undefined) {
      console.warn(`Tenant ID indefinido ao tentar navegar para ${module}`)
      return
    }
    console.log(`📋 Navegando para ${module} do tenant ${tenantId}`);
    navigateToPage(module, { mode: 'list', tenantId: String(tenantId) });
  }, [navigateToPage]);

  // Função para mostrar detalhes do tenant
  const handleShowDetails = useCallback((tenant: Tenant) => {
    setSelectedTenant(tenant);
    setOpenDetailsDialog(true);
  }, []);

  // Função para deletar tenant
  const handleDelete = useCallback(async (tenantId: number) => {
    if (!window.confirm(t("tenants.confirmDelete"))) {
      return
    }

    try {
      await api.delete(`/tenant/${tenantId}`)
      console.log("Proprietário excluído com sucesso")
      
      if (openDetailsDialog) {
        setOpenDetailsDialog(false)
      }
      
      setRefreshTrigger((prev) => prev + 1)
    } catch (err) {
      console.error("Erro ao excluir proprietário:", err)
      alert("Ocorreu um erro ao excluir o proprietário. Por favor, tente novamente.")
    }
  }, [openDetailsDialog]);

  // Colunas para a tabela
  const columns = useMemo(() => [
    {
      id: "tenantId",
      label: t("fields.id"),
      minWidth: 70,
      width: "8%",
      align: "center" as const,
      mobileHideLabel: true,
    },
    { 
      id: "tenantName", 
      label: t("fields.name"), 
      minWidth: 150
    },
    { 
      id: "tenantNickName",
      label: t("fields.nickname"),
      minWidth: 120,
      format: (value: string) => value || "-",
      // visível no mobile (apelido — info textual curta útil)
    },
    {
      // Parceiro a que o proprietário pertence. Enquanto o backend não
      // devolver o campo, a coluna mostra "—" — o que já diz que o dado ainda
      // não existe, em vez de esconder a informação.
      // No PROPRIETÁRIO os campos são tenantPartnerId / tenantPartnerName.
      // Sem reserva cruzada: ler `partnerId` aqui seria o campo de OUTRA
      // entidade. O nome vem derivado do id pelo servidor.
      id: "tenantPartnerId",
      label: t("fields.partner"),
      minWidth: 120,
      hide: isMobile,
      format: (_value: unknown, row: any) => {
        const nome = row?.tenantPartnerName?.toString().trim()
        if (nome) return nome
        const id = normalizarId(row?.tenantPartnerId)
        return id === null ? "—" : String(id)
      },
    },
    {
      id: "tenantTypeName",
      label: t("fields.type"),
      minWidth: 120,
      mobileHideLabel: true, // badge de tipo (PF/PJ), sem rótulo no mobile
      format: (value: string) => value || "-",
    },
    {
      id: "tenantStatus",
      label: t("fields.status"),
      minWidth: 90,
      width: "10%",
      align: "center" as const,
      mobileHideLabel: true,
      // Campo ausente (registro anterior à coluna) conta como ATIVO — um
      // proprietário sem o dado não pode aparecer como desligado.
      format: (value: number | undefined) => (
        <Chip
          label={value === 0 ? t("common.inactive") : t("common.active")}
          color={value === 0 ? "error" : "success"}
          size="small"
          variant="outlined"
        />
      ),
    },
  ], [t, isMobile, isTablet]);

  // Função para transformar os dados antes de exibir
  const transformData = useCallback((data: any[]): Tenant[] => {
    const normalizados = data.map(tenant => {
      return {
        ...tenant,
        // Garantir que tenantId é sempre um número
        tenantId: typeof tenant.tenantId === 'number' ? tenant.tenantId : 
                 typeof tenant.id === 'number' ? tenant.id : 0
      };
    });

    // O GET /tenant já vem recortado pelo servidor, pelo token: cada um
    // recebe só os proprietários que lhe cabem. Repetir o recorte aqui não
    // acrescentaria isolamento — a resposta já é a certa — e esconderia
    // proprietários válidos sem o campo de parceiro preenchido.
    //
    // O filtro por parceiro desta tela continua existindo, mas é ESCOLHA do
    // usuário (ver filtrosDeGrupo), não recorte de segurança.
    return normalizados;
  }, []);

  /* As linhas SÃO proprietários, então só faz sentido filtrar por
         parceiro — e só para quem não pertence a um. */
  const filtrosDeGrupo = useMemo<FiltroCampo[]>(() => ([
      {
        id: "tenantPartnerId",
        label: t("fields.partner"),
        rotulo: (row: any) => row?.tenantPartnerName,
        exigeVazio: "partner",
      },
    ]), [t])

  return (
    <>
      <EntityList
        title={t("nav.tenants")}
        endpoint="/tenant"
        columns={columns}
        addPath="" // Não usado com navegação criptografada
        editPathPrefix="" // Não usado com navegação criptografada
        idField="tenantId"
        dataPath="tenants"
        entityType="tenants"
        onRowClick={handleShowDetails}
        refreshTrigger={refreshTrigger}
        transformData={transformData}
      filterFields={filtrosDeGrupo}
        useEncryptedNavigation={true}
        encryptedPageName="proprietarios"
        onAddClick={handleNew}
        onEditClick={handleEdit}
      />

      {/* Diálogo de Detalhes do Tenant */}
      <Dialog 
        open={openDetailsDialog} 
        onClose={() => setOpenDetailsDialog(false)}
        maxWidth="md"
        fullWidth
        fullScreen={fullScreen}
      >
        {selectedTenant && (
          <>
            <DialogTitle
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                p: { xs: 2, sm: 3 },
              }}
            >
              <Typography variant="h6" sx={{ fontSize: { xs: "1rem", sm: "1.25rem" } }}>
                {selectedTenant.tenantName}
              </Typography>
              <IconButton edge="end" color="inherit" onClick={() => setOpenDetailsDialog(false)} aria-label="close">
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers sx={{ p: { xs: 2, sm: 3 } }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ fontSize: { xs: "0.9rem", sm: "1rem" } }}>
                    Informações Básicas
                  </Typography>
                  <Box sx={{ mt: 1, mb: 2 }}>
                    <Typography sx={{ fontSize: { xs: "0.85rem", sm: "1rem" } }}>
                      <strong>{t("fields.id")}:</strong> {selectedTenant.tenantId}
                    </Typography>
                    <Typography sx={{ fontSize: { xs: "0.85rem", sm: "1rem" } }}>
                      <strong>{t("fields.name")}:</strong> {selectedTenant.tenantName}
                    </Typography>
                    <Typography sx={{ fontSize: { xs: "0.85rem", sm: "1rem" } }}>
                      <strong>{t("fields.nickname")}:</strong> {selectedTenant.tenantNickName || '-'}
                    </Typography>
                    <Typography sx={{ fontSize: { xs: "0.85rem", sm: "1rem" } }}>
                      <strong>{t("fields.type")}:</strong> {selectedTenant.tenantTypeName || '-'}
                    </Typography>
                    <Typography sx={{ fontSize: { xs: "0.85rem", sm: "1rem" } }}>
                      <strong>{t("fields.fiscalCode")}:</strong> {selectedTenant.tenantFiscalCode || '-'}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ fontSize: { xs: "0.9rem", sm: "1rem" } }}>
                    Contato
                  </Typography>
                  <Box sx={{ mt: 1, mb: 2 }}>
                    <Typography sx={{ fontSize: { xs: "0.85rem", sm: "1rem" } }}>
                      <strong>{t("fields.phone")}:</strong> {selectedTenant.tenantPhone || '-'}
                    </Typography>
                  </Box>
                  <Box sx={{ mt: 1 }}>
                    <Typography sx={{ fontSize: { xs: "0.85rem", sm: "1rem" } }}>
                      <strong>{t("fields.address")}:</strong> {selectedTenant.tenantAddress || '-'}
                    </Typography>
                    <Typography sx={{ fontSize: { xs: "0.85rem", sm: "1rem" } }}>
                      <strong>{t("fields.city")}:</strong> {selectedTenant.tenantCity || '-'}
                    </Typography>
                    <Typography sx={{ fontSize: { xs: "0.85rem", sm: "1rem" } }}>
                      <strong>{t("fields.state")}:</strong> {selectedTenant.tenantState || '-'}
                    </Typography>
                    <Typography sx={{ fontSize: { xs: "0.85rem", sm: "1rem" } }}>
                      <strong>{t("fields.zipcode")}:</strong> {selectedTenant.tenantZipcode || '-'}
                    </Typography>
                    <Typography sx={{ fontSize: { xs: "0.85rem", sm: "1rem" } }}>
                      <strong>{t("fields.country")}:</strong> {selectedTenant.tenantCountry || '-'}
                    </Typography>
                  </Box>
                </Grid>
                
                {/* Seção de Ações Rápidas */}
                <Grid item xs={12}>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ fontSize: { xs: "0.9rem", sm: "1rem" }, mt: 2 }}>
                    Módulos Disponíveis
                  </Typography>
                  <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        setOpenDetailsDialog(false);
                        handleNavigateToModule('estabulos', selectedTenant.tenantId);
                      }}
                    >
                      Estábulos
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        setOpenDetailsDialog(false);
                        handleNavigateToModule('cavalos', selectedTenant.tenantId);
                      }}
                    >
                      Cavalos
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        setOpenDetailsDialog(false);
                        handleNavigateToModule('cameras', selectedTenant.tenantId);
                      }}
                    >
                      {t('nav.cameras')}
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        setOpenDetailsDialog(false);
                        handleNavigateToModule('gravadores', selectedTenant.tenantId);
                      }}
                    >
                      Gravadores
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </DialogContent>
            {/* <DialogActions sx={{ p: { xs: 1.5, sm: 2 }, flexDirection: isMobile ? "column" : "row" }}>
              <Button 
                onClick={() => setOpenDetailsDialog(false)}
                variant="outlined"
                sx={{ mb: isMobile ? 1 : 0, width: isMobile ? "100%" : "auto" }}
              >
                Fechar
              </Button>
              <Button
                variant="outlined"
                color="primary"
                onClick={() => {
                  setOpenDetailsDialog(false);
                  handleEdit(selectedTenant.tenantId);
                }}
                sx={{ mb: isMobile ? 1 : 0, width: isMobile ? "100%" : "auto" }}
              >
                Editar
              </Button>
              <Button
                color="error"
                variant="outlined"
                onClick={() => {
                  setOpenDetailsDialog(false);
                  handleDelete(selectedTenant.tenantId);
                }}
                sx={{ width: isMobile ? "100%" : "auto" }}
              >
                Excluir
              </Button>
            </DialogActions> */}
          </>
        )}
      </Dialog>
    </>
  );
}